import React, { ReactNode, useState } from 'react';
import { Box, ButtonProps, Divider, Paper, Theme } from '@mui/material';
import Add from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { usePopupState } from 'material-ui-popup-state/hooks';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { SystemStyleObject } from '@mui/system';

import { IconTextButton } from '../icon-text-button';
import { OnboardingClasses } from '@travelity/web/src/onboarding';

export interface CardItemOption {
    label: string;
    onClick?: (v: Record<string, any>) => void;

    [k: string]: any;
}

export interface CardListContainerProps extends Partial<ButtonProps> {
    children?: ReactNode;
    className?: string;
    addClassName?: string;
    addOptionsClassName?: string;
    readonly?: boolean;
    items?: number;
    maxHeight?: number;
    noPlaceholder?: boolean;
    sx?: SystemStyleObject<Theme>;
    disableAdding?: boolean;
    addButtonText?: string;
    onAdd?: (initialState?: Record<string, any>, el?: HTMLElement) => void;
    addOptions?: CardItemOption[];
    error?: boolean;
}

export const CardListContainer: React.FC<CardListContainerProps> = props => {
    const {
        children,
        className,
        addClassName,
        addOptionsClassName,
        readonly,
        items = 0,
        maxHeight,
        noPlaceholder,
        sx,
        disableAdding,
        addButtonText = 'Add',
        onAdd,
        addOptions,
        error,
    } = props;
    const [scrollEl, setScrollEl] = useState<HTMLElement>();

    const popupState = usePopupState({
        variant: 'popover',
        popupId: 'demoMenu',
    });

    const showAddButton =
        !disableAdding && !readonly && (noPlaceholder || items > 0);

    return (
        <Box
            className={className}
            sx={{
                maxHeight,
                position: 'relative',
                '&': sx,
                '&&&': { mx: { sm: 0, xs: -2 } },
            }}
        >
            <Box
                component={maxHeight ? PerfectScrollbar : 'div'}
                sx={{ maxHeight, '.ps__rail-y': { opacity: 0.6 } }}
                containerRef={ref => {
                    setScrollEl(ref);
                }}
            >
                <Paper
                    sx={{
                        p: { sm: 2, xs: 2 },
                        height: noPlaceholder || items > 0 ? 'auto' : '244px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                        border: '1px solid transparent',
                        ...(showAddButton &&
                            items > 0 && {
                                pb: { sm: '60px', xs: '60px' },
                            }),
                        ...(error && {
                            borderColor: 'error.main',
                        }),
                        ...(readonly
                            ? {
                                  opacity: 0.6,
                                  pointerEvents: 'none',
                              }
                            : {}),
                        ...(readonly && !items
                            ? {
                                  opacity: 0,
                              }
                            : {}),
                    }}
                >
                    {children}
                    {!disableAdding &&
                        !readonly &&
                        !noPlaceholder &&
                        items === 0 && (
                            <IconTextButton
                                text={addButtonText}
                                icon={<Add />}
                                className={`add-button${
                                    addClassName ? ` ${addClassName}` : ''
                                }`}
                                data-testid="add-card"
                                {...(addOptions
                                    ? bindTrigger(popupState)
                                    : {
                                          onClick: () =>
                                              onAdd?.(undefined, scrollEl),
                                      })}
                            />
                        )}

                    <Menu
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        MenuListProps={{
                            sx: { width: popupState.anchorEl?.clientWidth },
                        }}
                        PaperProps={{
                            className: addOptionsClassName,
                        }}
                        {...bindMenu(popupState)}
                    >
                        {addOptions?.map(
                            ({ label, onClick, ...rest }, index) => (
                                <>
                                    {!!index && (
                                        <Divider
                                            sx={{ mx: 2, '&&&': { my: 0.5 } }}
                                        />
                                    )}
                                    <MenuItem
                                        sx={{
                                            justifyContent: 'center',
                                            color: '#2B395B',
                                            fontSize: '12px',
                                            margin: '0 16px',
                                            borderRadius: '4px',
                                            '&:before': {
                                                borderTop: index
                                                    ? '1px solid #DFE1ED'
                                                    : 0,
                                            },
                                            '&:hover': {
                                                backgroundColor: '#D7E7EB',
                                            },
                                        }}
                                        onClick={() => {
                                            popupState.close();
                                            if (onClick) onClick(rest);
                                            else onAdd?.(rest, scrollEl);
                                        }}
                                    >
                                        {label}
                                    </MenuItem>
                                </>
                            )
                        )}
                    </Menu>
                </Paper>
            </Box>
            {showAddButton && (
                <Box
                    sx={{
                        background: 'rgba(246, 246, 251, 0.75)',
                        pl: 1,
                        pr: 0.25,
                        py: 1.25,
                        width: 'calc(100% - 22px)',
                        position: 'absolute',
                        bottom: 0,
                        left: '8px',
                        ...(noPlaceholder && items === 0
                            ? {
                                  width: '100%',
                                  top: 0,
                                  p: 0,
                                  left: 0,
                                  background: 'transparent',
                              }
                            : {}),
                    }}
                >
                    <Button
                        sx={{
                            bgcolor: '#FFFFFF',
                        }}
                        variant="outlined"
                        className={`add-button${
                            addClassName ? ` ${addClassName}` : ''
                        }`}
                        fullWidth
                        {...(addOptions
                            ? bindTrigger(popupState)
                            : {
                                  onClick: () => onAdd?.(undefined, scrollEl),
                              })}
                        data-testid="add-card"
                    >
                        <Add /> {addButtonText}
                    </Button>
                </Box>
            )}
        </Box>
    );
};
